import { Container, type Texture } from 'pixi.js';
import { makeImageButton } from './Button';
import { EnemyCard, type EnemyCardData } from './EnemyCard';

// Vertical layout inside the tab content area: cards on top, a SELECT button
// under each card. All render-only, tune freely.
const BUTTON_HEIGHT = 28;
const CARD_BUTTON_GAP = 10;
const CONTENT_PADDING_Y = 6;
const BUTTON_FONT_SIZE = 13;
const BUTTON_TEXT_COLOR = 0x000000;

export type FightsTabOptions = {
  // Content area size given by GameWindow.
  width: number;
  height: number;
  // 1 to 3 opponents; cards are spread evenly across the width.
  enemies: EnemyCardData[];
  cardBody: Texture;
  labelBg: Texture;
  selectBtn: Texture;
  fontFamily?: string;
  textResolution: number;
  onSelect: (enemyIndex: number) => void;
};

export class FightsTab extends Container {
  constructor(opts: FightsTabOptions) {
    super();
    const { width: w, height: h, enemies, fontFamily, textResolution } = opts;

    const cardHeight = h - BUTTON_HEIGHT - CARD_BUTTON_GAP - CONTENT_PADDING_Y * 2;
    const count = Math.min(enemies.length, 3);

    enemies.slice(0, 3).forEach((data, i) => {
      const card = new EnemyCard({
        data,
        body: opts.cardBody,
        labelBg: opts.labelBg,
        height: cardHeight,
        fontFamily,
        textResolution,
      });
      // Even spread: 1 card -> center, 2 -> thirds, 3 -> quarters.
      const centerX = (w * (i + 1)) / (count + 1);
      card.position.set(centerX - card.cardWidth / 2, CONTENT_PADDING_Y);
      this.addChild(card);

      const button = this.buildSelectButton(opts, () => opts.onSelect(i));
      button.position.set(
        centerX,
        CONTENT_PADDING_Y + cardHeight + CARD_BUTTON_GAP + BUTTON_HEIGHT / 2,
      );
      this.addChild(button);
    });
  }

  private buildSelectButton(opts: FightsTabOptions, onTap: () => void): Container {
    return makeImageButton({
      texture: opts.selectBtn,
      label: 'SELECT',
      height: BUTTON_HEIGHT,
      fontFamily: opts.fontFamily,
      fontSize: BUTTON_FONT_SIZE,
      textColor: BUTTON_TEXT_COLOR,
      textResolution: opts.textResolution,
      letterSpacing: 2,
      onTap,
    });
  }
}
